import { useState, useEffect, useCallback, useRef } from 'react';
import window from 'window-or-global';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchConsentUrl,
    fetchInterview,
    fetchRetakeAvailable,
    fetchTaxProfile
} from '../actions/tax.actions';
import { resetInterviewFailed, resetInterviewCurrentUrl } from '../reducer/tax.reducer';
import { getLocale } from '../utils/local.utils';
import { selectConfigState } from '../selectors/config.selector';
import { useLocation } from 'react-router';
import { usePrizeActions } from './usePrizeActions';

const PENDING_TIMEOUT = 3000;

export enum INTERVIEW_STATUS {
    IDLE = 'idle',
    PENDING = 'pending',
    COMPLETED = 'completed',
    ERROR = 'error'
}

export const useInterviewState = () => {
    const location = useLocation<{ prizeId?: string; isRetake?: boolean; dob?: string }>();
    const dispatch = useDispatch();
    const locale = getLocale();
    const configState = useSelector(selectConfigState);
    const { getPrizes } = usePrizeActions();

    const [status, setStatus] = useState(INTERVIEW_STATUS.IDLE);
    const [shouldRedirect, setShouldRedirect] = useState(false);
    const [exitConfirmed, setExitConfirmed] = useState(false);

    // Track if component is mounted to prevent side effects after unmount

    const getConsentTaxInterviewUrl = useCallback(
        ({ locale, prizeId, dob }: { locale: string; prizeId: string; dob?: string }) => {
            dispatch(fetchConsentUrl({ prizeId, locale, dob }) as any);
        },
        [dispatch]
    );

    const refreshTaxInterviewUrl = useCallback(
        ({ locale, dob }: { locale: string; dob?: string }) => {
            dispatch(fetchInterview({ locale, dob }) as any);
        },
        [dispatch]
    );

    const startInterview = useCallback(() => {
        const { isRetake, prizeId, dob } = location?.state || {};
        if (isRetake) {
            // retake personal tax interview
            refreshTaxInterviewUrl({ locale, dob });
        } else {
            if (prizeId) {
                // sign in tax interview with prize id
                getConsentTaxInterviewUrl({ locale, prizeId, dob });
            }
        }
    }, [location?.state, locale, refreshTaxInterviewUrl, getConsentTaxInterviewUrl, dispatch]);

    const exitConfirmedCallback = useCallback(() => {
        setStatus(INTERVIEW_STATUS.PENDING);
        setTimeout(() => {
            setShouldRedirect(true);
            setExitConfirmed(true);
            setStatus(INTERVIEW_STATUS.COMPLETED);
            dispatch(fetchTaxProfile() as any);
            dispatch(fetchRetakeAvailable() as any);
            // Refresh prize step in prize detail page
            getPrizes();
        }, PENDING_TIMEOUT);
    }, [dispatch, getPrizes]);

    const isRetakeRef = useRef(location?.state?.isRetake);

    useEffect(() => {
        isRetakeRef.current = location?.state?.isRetake;
    }, [location?.state]);

    const receiveMessage = useCallback(
        (event: any) => {
            if (
                exitConfirmed ||
                event.origin !== configState.tiOrigin ||
                event.data !== 'exitConfirmed'
            ) {
                return;
            }
            exitConfirmedCallback();
        },
        [exitConfirmed, configState.tiOrigin, exitConfirmedCallback]
    );

    useEffect(() => {
        startInterview();
    }, []);

    // Setup message listener separately
    useEffect(() => {
        window.addEventListener('message', receiveMessage, false);

        return () => {
            dispatch(resetInterviewFailed());
            window.removeEventListener('message', receiveMessage, false);
            dispatch(resetInterviewCurrentUrl());
        };
    }, [receiveMessage, dispatch]);

    return {
        status,
        shouldRedirect,
        exitConfirmed,
        startInterview,
        exitConfirmedCallback
    };
};
